import { useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVerticalThin, ChevronLeft, DotsVertical, Trash } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { Todo, User } from "@acto/taskpane/core/domain";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import type { RecapTodoEdit } from "@acto/taskpane/field-sales-2/data-access/recap";
import { AssigneePickerSheet } from "@acto/taskpane/field-sales-2/components/patterns/AssigneePickerSheet";
import { useAssigneeToggle } from "@acto/taskpane/field-sales-2/data-access/todos/useAssigneeToggle";
import { PrimaryActionButton } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow/ui/PrimaryActionButton";
import { CustomerPill } from "./CustomerPill";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { EditFieldCard, EditFieldRow, EditFieldRowLeft, EditFieldRowValue } from "./EditField";
import { TodoDescriptionField } from "./TodoDescriptionField";
import { TodoDueDateField } from "./TodoDueDateField";
import { domainDateToPicker } from "./dateBridge";

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

const Footer = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 60%, rgba(240, 238, 237, 0))`,
}));

export interface RecapEditTodoScreenProps {
  customerName: string;
  customerReference: string;
  /** The todo being edited — supplies the initial description and due date. */
  todo: Todo;
  /** Commit the edited fields. `dueDate` is UTC-midnight (see `RecapTodoEdit`). */
  onSave: (changes: RecapTodoEdit) => void;
  /** Delete this todo (from the overflow menu). */
  onDelete: () => void;
  /** Leave the editor without saving. */
  onBack: () => void;
}

/**
 * Full-screen editor for a single recap todo. The description and the due-date picker are mutually
 * exclusive: opening the date picker collapses the description into a one-line row, and vice versa
 * (the only trigger for the collapse/expand is the picker opening or closing).
 * Presentational: holds the draft locally and emits it via `onSave`.
 */
export function RecapEditTodoScreen({
  customerName,
  customerReference,
  todo,
  onSave,
  onDelete,
  onBack,
}: RecapEditTodoScreenProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const { locale } = useLocale();

  const [description, setDescription] = useState(todo.description ?? todo.title ?? "");
  // DatePicker works in UTC-midnight; seed from the todo's (local-midnight) dueDate. The due date
  // is optional, so a todo without one starts unset (null) rather than defaulting to today.
  const [dueDate, setDueDate] = useState<Date | null>(() => (todo.dueDate ? domainDateToPicker(todo.dueDate) : null));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [assignee, setAssignee] = useState<User | null>(todo.assignee ?? null);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const assigneeRowRef = useRef<HTMLButtonElement>(null);

  const { selectedId: selectedAssigneeId, disabled: assigneeDisabled } = useAssigneeToggle(todo.client?.id, assignee);
  const assigneeChanged = (assignee?.id ?? null) !== (todo.assignee?.id ?? null);

  const dueDateLabel = dueDate ? formatDayMonthYear(dueDate, locale) : t("edit.dueDateEmpty");

  const canSave = description.trim().length > 0;

  return (
    <Page>
      <TopNav
        titlePlacement="leading"
        leading={
          <HeaderIconButton type="button" aria-label={t("a11y.back")} onClick={onBack}>
            <ChevronLeft width={18} height={18} />
          </HeaderIconButton>
        }
        title={<CustomerPill name={customerName} reference={customerReference} align="start" />}
        trailing={
          <MenuPopover
            trigger={
              <HeaderIconButton type="button" aria-label={t("a11y.options")}>
                <DotsVertical width={18} height={18} />
              </HeaderIconButton>
            }
            ariaLabel={t("edit.options")}
            items={[{ label: t("edit.deleteTodo"), icon: <Trash aria-hidden="true" />, onSelect: onDelete }]}
          />
        }
      />

      <Body>
        {/* Description: editable when the picker is closed, a one-line summary when it's open. */}
        <TodoDescriptionField
          description={description}
          collapsed={pickerOpen}
          onChange={setDescription}
          onExpand={() => setPickerOpen(false)}
        />

        {/* Due date: a one-line row when closed, the calendar when open. */}
        <TodoDueDateField
          value={dueDate}
          label={dueDateLabel}
          open={pickerOpen}
          onChange={(date) => {
            setDueDate(date);
            setPickerOpen(false);
          }}
          onOpen={() => setPickerOpen(true)}
        />

        {/* Assignee: opens the client-team picker. */}
        <EditFieldCard>
          <EditFieldRow
            ref={assigneeRowRef}
            type="button"
            onClick={() => setAssigneePickerOpen(true)}
            disabled={assigneeDisabled}
          >
            <EditFieldRowLeft>{t("edit.assignee")}</EditFieldRowLeft>
            <EditFieldRowValue>
              {assignee?.displayName ?? t("edit.assigneeEmpty")}
              {!assigneeDisabled && <ChevronGrabberVerticalThin aria-hidden="true" />}
            </EditFieldRowValue>
          </EditFieldRow>
        </EditFieldCard>
      </Body>

      <Footer>
        <PrimaryActionButton
          type="button"
          disabled={!canSave}
          onClick={() =>
            onSave({
              description: description.trim(),
              dueDate: dueDate ?? undefined,
              // Only carry the assignee when it changed, so an unrelated content edit never
              // re-validates (and re-writes) the current assignee.
              assignee: assignee && assigneeChanged ? assignee : undefined,
            })
          }
        >
          {t("edit.save")}
        </PrimaryActionButton>
      </Footer>

      <AssigneePickerSheet
        isOpen={assigneePickerOpen}
        onOpenChange={setAssigneePickerOpen}
        anchorRef={assigneeRowRef}
        clientId={todo.client?.id}
        selectedId={selectedAssigneeId}
        onSelect={(picked: User) => setAssignee(picked)}
      />
    </Page>
  );
}
